<template>
  <div class="limits-page">
    <!-- Painel de alterações em lote -->
    <section class="batch-panel">
      <div class="batch-panel-header">
        <div class="batch-panel-title">
          <i class="fa-solid fa-layer-group"></i>
          <div>
            <h3>Alterações em lote</h3>
            <p>Selecione os filtros, informe o limite e aplique em várias células de uma vez.</p>
          </div>
        </div>
        <div class="batch-legend">
          <span class="legend-chip"><strong>-1</strong> Ilimitado</span>
          <span class="legend-chip"><strong>0</strong> Tudo descarga</span>
          <span class="legend-chip"><strong>&gt;0</strong> Limite normal</span>
        </div>
      </div>

      <div class="batch-grid">
        <div class="batch-field batch-field-1">
          <label class="batch-label">
            <i class="fa-solid fa-trophy"></i>
            Loterias
          </label>
          <div class="multiselect-container">
            <MultiSelect
              v-model="selectedLotteries"
              :options="lotteryOptions"
              placeholder="Selecione as loterias"
              :showSelectAllButtons="true"
              :disabled="isReadOnly"
            />
          </div>
        </div>

        <div class="batch-field batch-field-2">
          <label class="batch-label">
            <i class="fa-solid fa-clock"></i>
            Detalhes / horários
          </label>
          <div class="multiselect-container">
            <MultiSelect
              v-model="selectedLotteryDetails"
              :options="lotteryDetailOptions"
              placeholder="Selecione os detalhes"
              :showSelectAllButtons="true"
              :disabled="isReadOnly"
            />
          </div>
        </div>

        <div class="batch-field batch-field-3">
          <label class="batch-label">
            <i class="fa-solid fa-gamepad"></i>
            Modalidades
          </label>
          <div class="multiselect-container">
            <MultiSelect
              v-model="selectedModalities"
              :options="modalityOptions"
              placeholder="Selecione as modalidades"
              :showSelectAllButtons="true"
              :disabled="isReadOnly"
            />
          </div>
        </div>

        <div class="batch-field batch-limit-field">
          <label class="batch-label">
            <i class="fa-solid fa-hand-holding-dollar"></i>
            Valor do limite
          </label>
          <div class="limit-input-row">
            <input
              class="limit-input"
              type="number"
              v-model="selectedLimit"
              :disabled="isReadOnly"
              :readonly="isReadOnly"
              @input="onFieldChanged"
              placeholder="Ex: 100, 0 ou -1"
            />
            <button
              type="button"
              class="reproduce-button"
              :disabled="isReadOnly"
              @click="reproduce()"
            >
              <i class="fa-solid fa-check-double"></i>
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Estado vazio -->
    <div v-if="filteredRegionLimitsParameter.length === 0" class="empty-state">
      <div class="empty-card">
        <i class="fa-solid fa-info-circle"></i>
        <h4>Nenhuma loteria ativa encontrada</h4>
        <p>
          Para configurar limites, ative as loterias desejadas na seção
          <strong>Loterias</strong>.
        </p>
        <p>Apenas loterias marcadas como <strong>Ativo</strong> aparecem aqui.</p>
      </div>
    </div>

    <!-- Acordeões por loteria -->
    <div v-else class="lottery-tables">
      <section
        v-for="lottery in loteries"
        :key="lottery.name"
        class="lottery-card accordion-item"
      >
        <button
          type="button"
          class="lottery-card-header accordion-header"
          :class="{ active: isLotteryExpanded(lottery.name) }"
          @click="toggleLotteryAccordion(lottery.name)"
        >
          <div class="lottery-card-title">
            <i
              class="fa-solid fa-chevron-right accordion-chevron"
              :class="{ rotated: isLotteryExpanded(lottery.name) }"
            ></i>
            <i class="fa-solid fa-trophy"></i>
            <h4>{{ lottery.name }}</h4>
          </div>
          <span class="lottery-card-meta">
            {{ lottery.details.length }} horário(s) · {{ getModalities().length }} modalidade(s)
          </span>
        </button>

        <div v-show="isLotteryExpanded(lottery.name)" class="accordion-content">
          <div class="table-scroll">
            <table class="limits-table">
              <thead>
                <tr>
                  <th class="modality-col sticky-col">Modalidade</th>
                  <th
                    v-for="detail in lottery.details"
                    :key="detail.id"
                    class="detail-col"
                  >
                    <span class="detail-name">{{ detail.name }}</span>
                    <span class="detail-sub">Limite</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="modality in getModalities()" :key="modality.modalityId">
                  <td class="modality-col sticky-col">
                    <span class="modality-name">{{ modality.modalityName }}</span>
                  </td>
                  <td
                    v-for="detail in lottery.details"
                    :key="`${modality.modalityId}-${detail.id}`"
                    class="value-col"
                  >
                    <input
                      type="number"
                      class="cell-limit-input"
                      :class="limitToneClass(getValue(modality.modalityId, detail.id))"
                      :value="getValue(modality.modalityId, detail.id)"
                      :disabled="isReadOnly"
                      :readonly="isReadOnly"
                      @change="setValueModel($event, modality.modalityId, detail.id)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="special-values-panel">
        <div class="special-values-header">
          <i class="fa-solid fa-circle-info"></i>
          <span>Como interpretar os valores</span>
        </div>
        <div class="special-values-grid">
          <div class="special-value-item tone-unlimited">
            <span class="special-value-badge">-1</span>
            <div>
              <strong>Ilimitado</strong>
              <p>Aceita todo valor e não cria descarga/redescarga.</p>
            </div>
          </div>
          <div class="special-value-item tone-zero">
            <span class="special-value-badge">0</span>
            <div>
              <strong>Sem limite local</strong>
              <p>Todo valor vai para descarga/redescarga.</p>
            </div>
          </div>
          <div class="special-value-item tone-normal">
            <span class="special-value-badge">&gt; 0</span>
            <div>
              <strong>Limite normal</strong>
              <p>Só o que exceder o valor vai para descarga/redescarga.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import MultiSelect from "@/components/utils/MultiSelect.vue";
import { toast } from "@/composables/useToast";

export default {
  components: { MultiSelect },
  props: {
    regionLimitsParameter: {
      type: Array,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    regionLotteriesParameter: {
      type: Array,
      default: () => []
    }
  },
  emits: ["field-changed"],
  data: () => ({
    loteries: [],
    lotteryOptions: [],
    lotteryDetailOptions: [],
    modalityOptions: [],
    selectedLotteries: [],
    selectedLotteryDetails: [],
    selectedModalities: [],
    selectedLimit: null,
    selected: null,
    expandedLotteries: {},
  }),
  computed: {
    filteredRegionLimitsParameter() {
      if (!this.regionLimitsParameter || this.regionLimitsParameter.length === 0) {
        return [];
      }

      if (!this.regionLotteriesParameter || this.regionLotteriesParameter.length === 0) {
        return this.regionLimitsParameter;
      }

      const activeLotteryDetailIds = new Set();

      this.regionLotteriesParameter.forEach(lottery => {
        if (lottery.isActive) {
          activeLotteryDetailIds.add(lottery.lotteryDetailId);
        }
      });

      if (activeLotteryDetailIds.size === 0) {
        return [];
      }

      return this.regionLimitsParameter.filter(limit =>
        activeLotteryDetailIds.has(limit.lotteryDetailId)
      );
    },
    selectedLotteryNames() {
      return (this.selectedLotteries || []).map(item => item.name || item.id).filter(Boolean);
    },
    selectedLotteryDetailNames() {
      return (this.selectedLotteryDetails || []).map(item => item.name || item.id).filter(Boolean);
    },
    selectedModalityNames() {
      return (this.selectedModalities || []).map(item => item.name || item.id).filter(Boolean);
    },
  },
  mounted() {
    this.buildLotteries();
  },
  watch: {
    regionLimitsParameter: {
      handler() {
        this.buildLotteries();
      },
      deep: true
    },
    regionLotteriesParameter: {
      handler() {
        this.buildLotteries();
      },
      deep: true
    },
    filteredRegionLimitsParameter: {
      handler() {
        this.buildLotteries();
      },
      deep: true
    }
  },
  methods: {
    isLotteryExpanded(lotteryName) {
      return !!this.expandedLotteries[lotteryName];
    },
    toggleLotteryAccordion(lotteryName) {
      this.expandedLotteries = {
        ...this.expandedLotteries,
        [lotteryName]: !this.expandedLotteries[lotteryName],
      };
    },
    limitToneClass(value) {
      const n = Number(value);
      if (n === -1) return "tone-unlimited";
      if (n === 0) return "tone-zero";
      if (n > 0) return "tone-normal";
      return "";
    },
    toSelectOptions(names) {
      return (names || []).map(name => ({
        id: name,
        name,
      }));
    },
    syncSelectedOptions(selected, options) {
      if (!Array.isArray(selected) || selected.length === 0) return [];
      const optionIds = new Set((options || []).map(o => o.id));
      return selected.filter(item => optionIds.has(item.id || item.name));
    },
    buildLotteries() {
      this.loteries = [];
      this.lotteryOptions = [];
      this.lotteryDetailOptions = [];
      this.modalityOptions = [];

      const parametersToUse = this.filteredRegionLimitsParameter;

      if (parametersToUse && parametersToUse.length > 0) {
        this.getLists(parametersToUse);
        let lotIds = parametersToUse.map((c) => c.lotteryId).filter(onlyUnique);

        lotIds.forEach((l) => {
          let name = parametersToUse.filter((c) => c.lotteryId == l).map((c) => c.lotteryName)[0];
          let detailsIds = parametersToUse
            .filter((c) => c.lotteryId == l)
            .sort((a, b) => {
              return a.lotteryDetailName < b.lotteryDetailName ? -1 : 1;
            })
            .map((c) => c.lotteryDetailId)
            .filter(onlyUnique);
          let details = [];

          detailsIds.forEach((d) => {
            let detailName = parametersToUse.filter((c) => c.lotteryDetailId == d).map((c) => c.lotteryDetailName)[0];
            let modalities = parametersToUse.filter((c) => c.lotteryDetailId == d);
            details.push({
              id: d,
              name: detailName,
              modalities: modalities,
            });
          });

          this.loteries.push({
            name: name,
            details: details,
          });
        });
      }

      this.selectedLotteries = this.syncSelectedOptions(this.selectedLotteries, this.lotteryOptions);
      this.selectedLotteryDetails = this.syncSelectedOptions(this.selectedLotteryDetails, this.lotteryDetailOptions);
      this.selectedModalities = this.syncSelectedOptions(this.selectedModalities, this.modalityOptions);
    },
    getModalities() {
      let modalities = [];
      const parametersToUse = this.filteredRegionLimitsParameter;

      parametersToUse
        .map(({ modalityId, modalityName }) => ({ modalityId, modalityName }))
        .filter(function (item) {
          var i = modalities.findIndex((x) => x.modalityId == item.modalityId && x.modalityName == item.modalityName);
          if (i <= -1) {
            modalities.push(item);
          }
          return null;
        });
      return modalities.sort(function (a, b) {
        return a.modalityName > b.modalityName ? 1 : -1;
      });
    },
    getValue(modalityId, lotteryDetailId) {
      const parametersToUse = this.filteredRegionLimitsParameter;
      let val = parametersToUse
        .filter((c) => c.lotteryDetailId == lotteryDetailId && c.modalityId == modalityId)
        .map((c) => c.payUp)[0];
      return val;
    },
    setValueModel(event, modalityId, lotteryDetailId) {
      if (this.isReadOnly) {
        return;
      }

      let val = event.target.value;
      let item = this.regionLimitsParameter.filter((c) => c.lotteryDetailId == lotteryDetailId && c.modalityId == modalityId)[0];
      item.payUp = val;
      this.onFieldChanged();
    },
    getLists(parametersToUse) {
      const lotteryNames = parametersToUse.map((c) => c.lotteryName).filter(onlyUnique);
      const lotteryDetailNames = parametersToUse
        .map((c) => c.lotteryDetailName)
        .filter(onlyUnique)
        .sort();
      const modalityNames = parametersToUse
        .map((c) => c.modalityName)
        .filter(onlyUnique)
        .sort();

      this.lotteryOptions = this.toSelectOptions(lotteryNames);
      this.lotteryDetailOptions = this.toSelectOptions(lotteryDetailNames);
      this.modalityOptions = this.toSelectOptions(modalityNames);
    },
    reproduce() {
      if (this.isReadOnly) {
        toast.warning("Você não tem permissão para editar os limites!");
        return;
      }

      if (
        this.selectedLotteryNames.length == 0 &&
        this.selectedLotteryDetailNames.length == 0 &&
        this.selectedModalityNames.length == 0
      ) {
        toast.warning("Selecione, ao menos, um parâmetro!");
      } else if (this.selectedLimit == null) {
        toast.warning("Informe o limite a ser reproduzido!");
      } else {
        const parametersToUse = this.filteredRegionLimitsParameter;
        let seletedItems = this.regionLimitsParameter.filter(
          (c) =>
            (this.selectedLotteryNames.includes(c.lotteryName) || this.selectedLotteryNames.length == 0) &&
            (this.selectedLotteryDetailNames.includes(c.lotteryDetailName) || this.selectedLotteryDetailNames.length == 0) &&
            (this.selectedModalityNames.includes(c.modalityName) || this.selectedModalityNames.length == 0) &&
            parametersToUse.some(param =>
              param.lotteryDetailId === c.lotteryDetailId &&
              param.modalityId === c.modalityId
            )
        );
        seletedItems.forEach((i) => {
          i.payUp = this.selectedLimit;
        });
        this.onFieldChanged();
        this.clearBatchInputs();
      }
    },
    clearBatchInputs() {
      this.selectedLotteries = [];
      this.selectedLotteryDetails = [];
      this.selectedModalities = [];
      this.selectedLimit = null;
    },
    onFieldChanged() {
      if (!this.isReadOnly) {
        this.$emit("field-changed");
      }
    },
  },
};

function onlyUnique(value, index, array) {
  return array.indexOf(value) === index;
}
</script>

<style scoped>
.limits-page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow: visible;
}

/* ===== Painel em lote ===== */
.batch-panel {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: var(--shadow-sm);
  overflow: visible;
}

.batch-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.batch-panel-title {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
}

.batch-panel-title > i {
  color: var(--primary-color);
  font-size: 1.25rem;
  margin-top: 0.15rem;
}

.batch-panel-title h3 {
  margin: 0 0 0.25rem;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.batch-panel-title p {
  margin: 0;
  color: var(--text-muted);
  font-size: 0.88rem;
  line-height: 1.4;
}

.batch-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.legend-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 0.78rem;
}

.legend-chip strong {
  color: var(--primary-color);
}

.batch-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  align-items: start;
  overflow: visible;
}

.batch-field {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.batch-field-1 { z-index: 40; }
.batch-field-2 { z-index: 30; }
.batch-field-3 { z-index: 20; }
.batch-limit-field { z-index: 10; }

.batch-field:has(.multiselect--active) {
  z-index: 100;
}

.batch-label {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 600;
}

.batch-label i {
  color: var(--accent-color, var(--primary-color));
  font-size: 0.8rem;
}

.limit-input-row {
  display: flex;
  gap: 0.6rem;
  align-items: stretch;
}

.limit-input {
  flex: 1;
  min-width: 0;
  background: var(--input-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  font-size: 0.95rem;
  color: var(--text-primary);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.limit-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--primary-color-10);
}

.limit-input:disabled,
.limit-input[readonly] {
  opacity: 0.65;
  cursor: not-allowed;
  background: var(--bg-secondary);
}

.reproduce-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  white-space: nowrap;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  background: var(--primary-color);
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease;
}

.reproduce-button:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}

.reproduce-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* ===== Multiselect (mesmo padrão das outras páginas) ===== */
.multiselect-container {
  position: relative !important;
  z-index: 10 !important;
  overflow: visible;
}

.multiselect-container:has(.multiselect--active) {
  z-index: 1000 !important;
}

/* ===== Empty ===== */
.empty-state {
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;
}

.empty-card {
  max-width: 480px;
  text-align: center;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--shadow-sm);
}

.empty-card i {
  font-size: 2.4rem;
  color: var(--accent-color, var(--primary-color));
  margin-bottom: 0.85rem;
}

.empty-card h4 {
  margin: 0 0 0.75rem;
  color: var(--text-primary);
  font-size: 1.15rem;
}

.empty-card p {
  margin: 0 0 0.5rem;
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.45;
}

/* ===== Tabelas ===== */
.lottery-tables {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.lottery-card {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.lottery-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.9rem 1.1rem;
  border: none;
  border-bottom: 1px solid transparent;
  background: var(--bg-secondary);
  flex-wrap: wrap;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;
}

.lottery-card-header:hover {
  background: var(--hover-color, color-mix(in srgb, var(--primary-color) 8%, var(--bg-secondary)));
}

.lottery-card-header.active {
  border-bottom-color: var(--border-color);
  background: color-mix(in srgb, var(--primary-color) 8%, var(--bg-secondary));
}

.lottery-card-title {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.lottery-card-title > i:not(.accordion-chevron) {
  color: var(--primary-color);
}

.accordion-chevron {
  color: var(--text-muted);
  font-size: 0.75rem;
  width: 0.85rem;
  transition: transform 0.2s ease, color 0.2s ease;
}

.accordion-chevron.rotated {
  transform: rotate(90deg);
  color: var(--primary-color);
}

.lottery-card-title h4 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
}

.lottery-card-meta {
  color: var(--text-muted);
  font-size: 0.8rem;
}

.accordion-content {
  border-top: 1px solid var(--border-color);
}

.table-scroll {
  overflow-x: auto;
}

.limits-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 640px;
}

.limits-table th,
.limits-table td {
  border-bottom: 1px solid var(--border-color);
  border-right: 1px solid var(--border-color);
  padding: 0.55rem 0.65rem;
  vertical-align: middle;
}

.limits-table th:last-child,
.limits-table td:last-child {
  border-right: none;
}

.limits-table thead th {
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-weight: 600;
  text-align: center;
}

.modality-col {
  text-align: left;
  min-width: 140px;
  background: var(--card-background);
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
}

.limits-table thead .sticky-col {
  z-index: 3;
  background: var(--bg-secondary);
}

.detail-col {
  min-width: 110px;
}

.detail-name {
  display: block;
  font-size: 0.88rem;
  color: var(--text-primary);
}

.detail-sub {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.modality-name {
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
}

.value-col {
  text-align: center;
  background: var(--card-background);
}

.cell-limit-input {
  width: 100%;
  max-width: 110px;
  margin: 0 auto;
  display: block;
  background: var(--input-background);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-primary);
  padding: 0.45rem 0.5rem;
  font-size: 0.9rem;
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.cell-limit-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px var(--primary-color-10);
}

.cell-limit-input:disabled,
.cell-limit-input[readonly] {
  opacity: 0.65;
  cursor: not-allowed;
  background: var(--bg-secondary);
}

.cell-limit-input.tone-unlimited {
  border-color: color-mix(in srgb, var(--primary-color) 45%, var(--border-color));
  background: var(--primary-color-10, color-mix(in srgb, var(--primary-color) 12%, transparent));
}

.cell-limit-input.tone-zero {
  border-color: color-mix(in srgb, #f0ad4e 50%, var(--border-color));
  background: color-mix(in srgb, #f0ad4e 12%, transparent);
}

.cell-limit-input.tone-normal {
  border-color: color-mix(in srgb, #28a745 45%, var(--border-color));
  background: color-mix(in srgb, #28a745 10%, transparent);
}

/* ===== Valores especiais ===== */
.special-values-panel {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1rem 1.15rem;
  box-shadow: var(--shadow-sm);
}

.special-values-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.95rem;
}

.special-values-header i {
  color: var(--accent-color, var(--primary-color));
}

.special-values-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.special-value-item {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.75rem;
  border-radius: 10px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
}

.special-value-item strong {
  display: block;
  color: var(--text-primary);
  font-size: 0.9rem;
  margin-bottom: 0.2rem;
}

.special-value-item p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.82rem;
  line-height: 1.4;
}

.special-value-badge {
  min-width: 42px;
  text-align: center;
  padding: 0.3rem 0.45rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--primary-color);
  background: var(--primary-color-10, color-mix(in srgb, var(--primary-color) 12%, transparent));
  flex-shrink: 0;
}

.special-value-item.tone-zero .special-value-badge {
  color: #b8860b;
  background: color-mix(in srgb, #f0ad4e 18%, transparent);
}

.special-value-item.tone-normal .special-value-badge {
  color: #1e7e34;
  background: color-mix(in srgb, #28a745 16%, transparent);
}

@media (max-width: 1200px) {
  .batch-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .batch-grid,
  .special-values-grid {
    grid-template-columns: 1fr;
  }

  .limit-input-row {
    flex-direction: column;
  }

  .reproduce-button {
    width: 100%;
  }
}
</style>
